/**
 * Admin login provider management hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminLoginsApi } from '../api/adminLoginsApi';
import type { Binding, Provider, ProviderRequestBody, TestConnection } from '../types';

const ADMIN_LOGIN_PROVIDERS_KEY = ['admin-login-providers'];
const ADMIN_LOGIN_PROVIDER_BINDINGS_KEY = ['admin-login-provider-bindings'];

export function useAdminLoginProviders() {
  return useQuery<Provider[]>({
    queryKey: ADMIN_LOGIN_PROVIDERS_KEY,
    queryFn: adminLoginsApi.listProviders,
  });
}

export function useCreateLoginProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ProviderRequestBody) => adminLoginsApi.createProvider(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_LOGIN_PROVIDERS_KEY });
    },
  });
}

export function useUpdateLoginProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ProviderRequestBody }) =>
      adminLoginsApi.updateProvider(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_LOGIN_PROVIDERS_KEY });
    },
  });
}

export function useDeleteLoginProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminLoginsApi.deleteProvider(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_LOGIN_PROVIDERS_KEY });
    },
  });
}

export function useTestLoginProvider() {
  return useMutation<TestConnection, unknown, string>({
    mutationFn: (id: string) => adminLoginsApi.testProvider(id),
  });
}

export function useProviderBindings(providerId: string | null) {
  return useQuery<Binding[]>({
    queryKey: [...ADMIN_LOGIN_PROVIDER_BINDINGS_KEY, providerId],
    queryFn: () => adminLoginsApi.listBindings(providerId as string),
    enabled: providerId !== null,
  });
}

export function useCreateBinding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ providerId, featureId }: { providerId: string; featureId: string }) =>
      adminLoginsApi.createBinding(providerId, featureId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...ADMIN_LOGIN_PROVIDER_BINDINGS_KEY, variables.providerId],
      });
    },
  });
}

export function useDeleteBinding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ providerId, bindingId }: { providerId: string; bindingId: string }) =>
      adminLoginsApi.deleteBinding(providerId, bindingId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...ADMIN_LOGIN_PROVIDER_BINDINGS_KEY, variables.providerId],
      });
    },
  });
}
